import type { ReactNode } from "react";

import clsx from "clsx";
import { Button, FieldError, Input, Label, NumberField, TextField } from "react-aria-components";

import styles from "./base-input.module.css";

type LabelInputAndErrorProps = {
	children: ReactNode;
	description?: string;
	errorMessage?: ReactNode;
	isInvalid?: boolean;
	label: string;
};

export function LabelInputAndError({
	children,
	description,
	errorMessage,
	isInvalid,
	label,
}: LabelInputAndErrorProps): ReactNode {
	return (
		<>
			<Label className={clsx("caption", "bold", styles.label)}>{label}</Label>
			{children}
			{isInvalid ? (
				<FieldError className={clsx("caption", styles.errorMessage)}>
					{errorMessage}
				</FieldError>
			) : (
				<span className={clsx("caption", styles.description)}>{description}</span>
			)}
		</>
	);
}

export type BaseInputProps = {
	className?: string;
	/**
	 * errorMessage replaces this field when visible
	 */
	description?: string;
	errorMessage?: ReactNode;
	formatOptions?: Partial<Intl.NumberFormatOptions>;
	isDisabled?: boolean;
	isInvalid?: boolean;
	isReadOnly?: boolean;
	label: string;
	maxLength?: number;
	maxValue?: number;
	minValue?: number;
	onChange?: (val: number | string) => void;
	onPress?: () => void;
	placeholder: string;
	slotLeft?: ReactNode;
	step?: number;
	type: "autocomplete" | "number" | "text";
	value?: number | string;
};

export function BaseInput({
	description,
	errorMessage,
	label,
	maxLength,
	maxValue,
	minValue,
	onChange,
	placeholder,
	slotLeft,
	step,
	type,
	value,
	...props
}: BaseInputProps): ReactNode {
	const className = clsx(props.className, styles.wrapper, props.isInvalid && styles.invalidField);

	if (type === "autocomplete") {
		return (
			<div className={className}>
				<Label className={clsx("caption", "bold", styles.label)}>{label}</Label>
				<Button
					className={clsx(
						styles.input,
						styles.trigger,
						!value && styles.placeholderColor,
						props.isReadOnly && styles.readOnly,
					)}
					data-readonly={props.isReadOnly || undefined}
					isDisabled={props.isDisabled}
					onPress={props.isReadOnly ? undefined : props.onPress}
				>
					{value ? slotLeft : null}
					<span className={styles.triggerValue}>{value || placeholder}</span>
				</Button>
				{props.isInvalid ? (
					<span className={clsx("caption", styles.errorMessage)}>{errorMessage}</span>
				) : (
					<span className={clsx("caption", styles.description)}>{description}</span>
				)}
			</div>
		);
	}
	if (type === "number") {
		return (
			<NumberField
				{...props}
				className={className}
				maxValue={maxValue}
				minValue={minValue}
				onChange={onChange}
				step={step}
				value={value as number}
			>
				<LabelInputAndError
					description={description}
					errorMessage={errorMessage}
					isInvalid={props.isInvalid}
					label={label}
				>
					<Input className={clsx(styles.input, "body-2")} placeholder={placeholder} />
				</LabelInputAndError>
			</NumberField>
		);
	}

	return (
		<TextField {...props} className={className} onChange={onChange} value={value as string}>
			<LabelInputAndError
				description={description}
				errorMessage={errorMessage}
				isInvalid={props.isInvalid}
				label={label}
			>
				<Input
					className={clsx(styles.input, "body-2")}
					maxLength={maxLength}
					placeholder={placeholder}
				/>
			</LabelInputAndError>
		</TextField>
	);
}
